import React from 'react';

/**
 * Marcador de estado do design system (at-56, frente B).
 *
 * 🔴 **O chip SEMPRE tem texto — nunca é só cor.** Isso não é preferência
 * estética: é requisito da spec 02 §9 (*"legíveis sem depender só de cor —
 * daltonismo, sol na tela"*) e foi decisão de Victor na at-54, quando a situação
 * no SGA passou a ser dita por extenso no `ModalDetalhesAdesao`. É por isso que
 * este componente **substitui** as bolinhas `.adesoes-ponto*` (10px de cor pura,
 * cujo único texto vivia num `title` que o celular não mostra) e por isso o
 * design system **não** ganhou um `StatusDot`, apesar de o índice o prever.
 *
 * ⚠️ `pendente` é ÂMBAR, não vermelho — e essa troca é deliberada. A spec 02 §2.3
 * é explícita: *"vermelho não é erro — é 'ainda não foi enviado ao SGA'… escolha
 * uma linguagem visual que não pareça alarme: a maioria dos registros novos passa
 * alguns instantes em vermelho por natureza"*. O token `amber-*` existe no kit
 * justamente para pendência administrativa. Vermelho fica reservado ao `perigo`,
 * que é falha de verdade (veículo excluído, por exemplo).
 */

export type VarianteChip = 'ok' | 'pendente' | 'perigo' | 'neutro' | 'marca';

const POR_VARIANTE: Record<VarianteChip, string> = {
  ok: 'tw-bg-success-bg tw-text-success tw-border-success/40',
  /* pendência — ver o aviso acima sobre por que não é vermelho */
  pendente: 'tw-bg-amber-bg tw-text-amber-warn tw-border-amber-warn/40',
  perigo: 'tw-bg-danger-bg tw-text-danger tw-border-danger/40',
  neutro: 'tw-bg-superficie tw-text-texto-suave tw-border-superficie-borda',
  marca: 'tw-bg-brand-tint tw-text-brand-deep tw-border-brand/40',
};

interface PropsChip extends React.HTMLAttributes<HTMLSpanElement> {
  variante?: VarianteChip;
  /** O rótulo. Obrigatório: chip sem texto é bolinha de cor — ver o aviso acima. */
  children: React.ReactNode;
}

const Chip: React.FC<PropsChip> = ({ variante = 'neutro', className = '', children, ...resto }) => (
  <span
    className={[
      'tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-border',
      'tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-leading-5',
      POR_VARIANTE[variante],
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    {...resto}
  >
    {children}
  </span>
);

export default Chip;
